import { ProductSelectEvent } from '@shopify/events';

/**
 * Dose Buy Box Selector Web Component
 * Combines Variant Picker (Size/Pack Size), Skio Subscription Plan Picker, and Add to Cart CTA.
 */
class DoseBuyboxSelector extends HTMLElement {
  connectedCallback() {
    try {
      this.variantsData = JSON.parse(this.getAttribute('data-variants-json') || '{}');
      this.pricesData = JSON.parse(this.getAttribute('data-prices-json') || '{}');
    } catch (err) {
      console.error('Failed to parse buybox JSON data:', err);
      this.variantsData = {};
      this.pricesData = {};
    }

    this.productForm = this.querySelector('product-form-component');
    this.form = this.querySelector('form[data-type="add-to-cart-form"]');
    this.variantIdInput = this.querySelector('input[name="id"]');
    this.sellingPlanInput = this.querySelector('input[ref="sellingPlanInput"], input[name="selling_plan"]');
    
    this.pills = Array.from(this.querySelectorAll('.dose-buybox-form__pill[data-option-index]'));
    this.radios = Array.from(this.querySelectorAll('input[name="purchase_type"]'));
    
    this.pills.forEach(pill => {
      pill.addEventListener('click', (e) => this.onPillClick(e));
    });
    

    
    // Initial synchronization of form state
    this.syncState();
  }
  
  onPillClick(e) {
    const pill = e.currentTarget;
    const optionIdx = pill.getAttribute('data-option-index');
    
    // Update active state among siblings in the same option group
    this.pills.forEach(p => {
      if (p.getAttribute('data-option-index') === optionIdx) {
        p.classList.remove('dose-buybox-form__pill--active');
        p.setAttribute('aria-pressed', 'false');
      }
    });
    pill.classList.add('dose-buybox-form__pill--active');
    pill.setAttribute('aria-pressed', 'true');
    
    this.updateVariantSelection(true);
  }
  
  updateVariantSelection(isUserAction = false) {
    // Gather selected values across all option indexes
    const selectedValues = [];
    this.pills.forEach(p => {
      if (p.classList.contains('dose-buybox-form__pill--active')) {
        const idx = parseInt(p.getAttribute('data-option-index'), 10);
        selectedValues[idx] = p.getAttribute('data-value');
      }
    });
    
    // Filter sub-options based on top level selection (index 0)
    if (selectedValues[0]) {
      const validSubOptions = new Set();
      // Only show sub-options that exist for this top-level value
      Object.values(this.variantsData).forEach(v => {
        if (v.options && v.options[0] === selectedValues[0]) {
          validSubOptions.add(v.options[1]);
        }
      });

      let firstValidPill = null;
      let selectedValid = false;
      
      this.pills.forEach(p => {
        if (p.getAttribute('data-option-index') === '1') {
          const val = p.getAttribute('data-value');
          if (validSubOptions.has(val)) {
            p.style.display = ''; // Show
            if (!firstValidPill) firstValidPill = p;
            if (p.classList.contains('dose-buybox-form__pill--active')) {
              selectedValid = true;
            }
          } else {
            p.style.display = 'none'; // Hide
            p.classList.remove('dose-buybox-form__pill--active');
            p.setAttribute('aria-pressed', 'false');
          }
        }
      });

      // If current selection is hidden, select the first valid option
      if (!selectedValid && firstValidPill) {
        firstValidPill.classList.add('dose-buybox-form__pill--active');
        firstValidPill.setAttribute('aria-pressed', 'true');
        selectedValues[1] = firstValidPill.getAttribute('data-value');
      }
    }
    
    // Find matching variant
    const matchingVariant = Object.values(this.variantsData).find(v => {
      if (!v.options || !Array.isArray(v.options)) return false;
      // Use slice to only compare the number of selected values
      return v.options.slice(0, selectedValues.length).every((optVal, idx) => optVal === selectedValues[idx]);
    });
    
    if (matchingVariant && this.variantIdInput) {
      this.variantIdInput.value = matchingVariant.id;
      
      const priceInfo = this.pricesData[matchingVariant.id];
      if (priceInfo) {
        this.updateCtaButton(priceInfo.available);
      }
      
      // Dispatch events for native theme components listening for variant changes
      this.dispatchEvent(new CustomEvent('variant:change', { detail: { variant: matchingVariant }, bubbles: true }));
      this.variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));

      // Dispatch Shopify Standard Storefront Event (shopify:product:select) on user interaction
      if (isUserAction) {
        try {
          const optionGroups = Array.from(this.querySelectorAll('.dose-buybox-form__option-group[data-option-index]'));
          const selectedOptions = optionGroups
            .map((group) => {
              const idx = parseInt(group.getAttribute('data-option-index'), 10);
              const name = group.getAttribute('data-option-name') || `Option ${idx + 1}`;
              const value = selectedValues[idx];
              return value ? { name, value } : null;
            })
            .filter(Boolean);

          if (selectedOptions.length > 0) {
            const productId = this.getAttribute('data-product-id') || '';
            const productTitle = this.getAttribute('data-product-title') || '';
            const productHandle = this.getAttribute('data-product-handle') || '';
            const currencyCode = this.getAttribute('data-currency') || window.Shopify?.currency?.active || 'USD';
            const priceAmount =
              typeof matchingVariant.price_cents === 'number'
                ? (matchingVariant.price_cents / 100).toFixed(2)
                : '0.00';

            const deferredEventPromise = ProductSelectEvent.createPromise();
            this.dispatchEvent(
              new ProductSelectEvent({
                product: {
                  id: productId,
                  title: productTitle,
                  handle: productHandle,
                },
                selectedOptions,
                promise: deferredEventPromise.promise,
              })
            );

            deferredEventPromise.resolve({
              variant: {
                id: matchingVariant.id,
                title: matchingVariant.title,
                availableForSale: Boolean(matchingVariant.available),
                price: {
                  amount: priceAmount,
                  currencyCode,
                },
                selectedOptions,
              },
              detail: {
                productId: String(productId),
                resource: {
                  id: matchingVariant.id,
                  sku: matchingVariant.sku || undefined,
                },
              },
            });
          }
        } catch (err) {
          console.error('Failed to dispatch ProductSelectEvent:', err);
        }
      }
    }
  }

  
  updateCtaButton(isAvailable) {
    const ctaBtn = this.querySelector('.dose-buybox-form__submit-btn');
    if (ctaBtn) {
      if (isAvailable) {
        ctaBtn.disabled = false;
        ctaBtn.textContent = ctaBtn.getAttribute('data-default-text') || 'Add to cart';
      } else {
        ctaBtn.disabled = true;
        ctaBtn.textContent = 'Sold out';
      }
    }
  }
  
  syncState() {
    this.updateVariantSelection(false);
  }
}

if (!customElements.get('dose-buybox-selector')) {
  customElements.define('dose-buybox-selector', DoseBuyboxSelector);
}
